import { ApiServiceWithSettings, Audience, Authentication } from '../../common'
import { AxiosPromise } from 'axios'
import { Documents, Invoices, Pagination } from '@one/types'
import qs from 'qs'

export class BackofficeInvoicesApi extends ApiServiceWithSettings {
  getInvoices({
    pageNumber,
    pageSize,
    search,
    paymentFilter,
    sortingMode,
    operatorName,
    clientId,
    startDate,
    endDate,
    branchId
  }: {
    pageNumber: number
    pageSize: number
    search: string | null
    paymentFilter: Invoices.InvoiceFilterEnum
    sortingMode: Invoices.SortingModeEnum
    operatorName?: string
    clientId?: string
    startDate?: string
    endDate?: string
    branchId?: string
  }): AxiosPromise<Pagination.Pager<Invoices.InvoicePagination>> {
    return this.get('/invoices', {
      params: {
        pageNumber,
        pageSize,
        search: search || null,
        clientId,
        paymentFilter: paymentFilter || 'ALL',
        sortingMode: sortingMode || 'NEWEST_TO_OLDEST',
        operatorName,
        startDate,
        endDate,
        branchId
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getSingleInvoice(invoiceNumber: string): AxiosPromise<Invoices.Invoice> {
    return this.get(`/invoices/single?number=${invoiceNumber}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getInvoicesPaymentData(
    document: Array<string>,
    clientId: string | null
  ): AxiosPromise<Documents.DocumentSums> {
    return this.get(`/invoices/sums`, {
      params: {
        document,
        clientId: clientId || undefined
      },
      paramsSerializer: function(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' })
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getInvoicesCorrectionsPaymentData(
    document: Array<string>,
    clientId: string | null
  ): AxiosPromise<Invoices.InvoiceSums> {
    return this.get(`/invoice-corrections/sums`, {
      params: {
        document,
        clientId: clientId || undefined
      },
      paramsSerializer: function(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' })
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getReceiptsPaymentData(
    document: Array<string>,
    clientId: string | null
  ): AxiosPromise<Invoices.InvoiceSums> {
    return this.get(`/fiscal-receipts/sums`, {
      params: {
        document,
        clientId: clientId || undefined
      },
      paramsSerializer: function(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' })
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getReceiptsCorrectionsPaymentData(
    document: Array<string>,
    clientId: string | null
  ): AxiosPromise<Invoices.InvoiceSums> {
    return this.get(`/fiscal-receipt-corrections/sums`, {
      params: {
        document,
        clientId: clientId || undefined
      },
      paramsSerializer: function(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' })
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getSuggestDocumentsList(
    pageNumber: number,
    pageSize: number,
    query: string | null,
    clientId: string | null
  ): AxiosPromise<Documents.DocumentsPagination> {
    return this.get('/accounting-documents/suggest', {
      params: {
        pageNumber: pageNumber || 1,
        pageSize: pageSize || 10,
        query: query || null,
        clientId: clientId || null
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getAllNumbersInvoices({
    search,
    paymentFilter,
    clientId,
    startDate,
    endDate,
    operatorName,
    branchId
  }: {
    search: string | null
    paymentFilter: Invoices.InvoiceFilterEnum
    clientId?: string
    startDate?: string
    endDate?: string
    operatorName?: string
    branchId?: string
  }): AxiosPromise<Documents.DocumentSums> {
    return this.get(`/invoices/sums`, {
      params: {
        clientId,
        search: search || null,
        paymentFilter: paymentFilter || 'ALL',
        startDate,
        endDate,
        operatorName,
        branchId
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getAllNumbersInvoicesCorrections({
    search,
    paymentFilter,
    clientId,
    startDate,
    endDate,
    operatorName,
    branchId
  }: {
    search: string | null
    paymentFilter: Invoices.InvoiceFilterEnum
    clientId?: string
    startDate?: string
    endDate?: string
    operatorName?: string
    branchId?: string
  }): AxiosPromise<Invoices.InvoiceNumbers> {
    return this.get(`/invoice-corrections/sums`, {
      params: {
        clientId,
        search: search || null,
        paymentFilter: paymentFilter || 'ALL',
        startDate,
        endDate,
        operatorName,
        branchId
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getAllNumbersReceipts({
    search,
    paymentFilter,
    clientId,
    startDate,
    endDate,
    operatorName,
    branchId
  }: {
    search: string | null
    paymentFilter: Invoices.InvoiceFilterEnum
    clientId?: string
    startDate?: string
    endDate?: string
    operatorName?: string
    branchId?: string
  }): AxiosPromise<Invoices.InvoiceNumbers> {
    return this.get(`/fiscal-receipts/sums`, {
      params: {
        clientId,
        search: search || null,
        paymentFilter: paymentFilter || 'ALL',
        startDate,
        endDate,
        operatorName,
        branchId
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getAllNumbersReceiptsCorrections({
    search,
    paymentFilter,
    clientId,
    startDate,
    endDate,
    operatorName,
    branchId
  }: {
    search: string | null
    paymentFilter: Invoices.InvoiceFilterEnum
    clientId?: string
    startDate?: string
    endDate?: string
    operatorName?: string
    branchId?: string
  }): AxiosPromise<Invoices.InvoiceNumbers> {
    return this.get(`/fiscal-receipt-corrections/sums`, {
      params: {
        clientId,
        search: search || null,
        paymentFilter: paymentFilter || 'ALL',
        startDate,
        endDate,
        operatorName,
        branchId
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  exportInvoice(number: string): AxiosPromise<BlobPart> {
    return this.get('/invoices/export', {
      params: {
        number,
        'one-tenant': this.getTenantKey()
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      responseType: 'blob'
    })
  }

  exportInvoiceCorrections(number: string): AxiosPromise<BlobPart> {
    return this.get('/invoice-corrections/export', {
      params: {
        number,
        'one-tenant': this.getTenantKey()
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      responseType: 'blob'
    })
  }

  exportFiscalReceipts(number: string): AxiosPromise<BlobPart> {
    return this.get('/fiscal-receipts/export', {
      params: {
        number,
        'one-tenant': this.getTenantKey()
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      responseType: 'blob'
    })
  }

  exportFiscalReceiptCorrections(number: string): AxiosPromise<BlobPart> {
    return this.get('/fiscal-receipt-corrections/export', {
      params: {
        number,
        'one-tenant': this.getTenantKey()
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      responseType: 'blob'
    })
  }

  getSumsInvoices(
    pageNumber: number,
    pageSize: number,
    clientId?: string,
    endDate?: string,
    invoiceFilter?: Invoices.InvoiceFilterEnum,
    invoiceTypeFilter?: Invoices.InvoiceTypeEnum,
    startDate?: string
  ): AxiosPromise<Pagination.Pager<Invoices.Backoffice.InvoicesAmount>> {
    return this.get('/invoice/amounts', {
      params: {
        pageNumber,
        pageSize,
        clientId,
        invoiceFilter: invoiceFilter || 'ALL',
        invoiceTypeFilter: invoiceTypeFilter || 'ALL',
        endDate,
        startDate
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getInvoiceCorrections({
    pageNumber,
    pageSize,
    search,
    paymentFilter,
    sortingMode,
    operatorName,
    clientId,
    startDate,
    endDate,
    branchId
  }: {
    pageNumber: number
    pageSize: number
    search: string | null
    paymentFilter: Invoices.InvoiceFilterEnum
    sortingMode: Invoices.SortingModeEnum
    operatorName?: string
    clientId?: string
    startDate?: string
    endDate?: string
    branchId?: string
  }): AxiosPromise<Pagination.Pager<Invoices.InvoicePagination>> {
    return this.get('/invoice-corrections', {
      params: {
        pageNumber,
        pageSize,
        search: search || null,
        clientId,
        paymentFilter: paymentFilter || 'ALL',
        sortingMode: sortingMode || 'NEWEST_TO_OLDEST',
        operatorName,
        startDate,
        endDate,
        branchId
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getSingleInvoiceCorrection(
    correctionNumber: string
  ): AxiosPromise<Invoices.Invoice> {
    return this.get(`/invoice-corrections/single?number=${correctionNumber}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  createInvoiceCorrection(payload: any): AxiosPromise<any> {
    return this.post('/invoice-corrections', payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getFiscalReceipts({
    pageNumber,
    pageSize,
    search,
    paymentFilter,
    sortingMode,
    operatorName,
    clientId,
    startDate,
    endDate,
    branchId
  }: {
    pageNumber: number
    pageSize: number
    search: string | null
    paymentFilter: Invoices.InvoiceFilterEnum
    sortingMode: Invoices.SortingModeEnum
    operatorName?: string
    clientId?: string
    startDate?: string
    endDate?: string
    branchId?: string
  }): AxiosPromise<Pagination.Pager<Invoices.InvoicePagination>> {
    return this.get('/fiscal-receipts', {
      params: {
        pageNumber,
        pageSize,
        search: search || null,
        clientId,
        paymentFilter: paymentFilter || 'ALL',
        sortingMode: sortingMode || 'NEWEST_TO_OLDEST',
        operatorName,
        startDate,
        endDate,
        branchId
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getSingleFiscalReceipts(
    fiscalReceiptNumber: string
  ): AxiosPromise<Invoices.Invoice> {
    return this.get(`/fiscal-receipts/single?number=${fiscalReceiptNumber}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  createFiscalReceipt(payload: any) {
    return this.post('/fiscal-receipts', payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getFiscalReceiptCorrections({
    pageNumber,
    pageSize,
    search,
    paymentFilter,
    sortingMode,
    operatorName,
    clientId,
    startDate,
    endDate,
    branchId
  }: {
    pageNumber: number
    pageSize: number
    search: string | null
    paymentFilter: Invoices.InvoiceFilterEnum
    sortingMode: Invoices.SortingModeEnum
    operatorName?: string
    clientId?: string
    startDate?: string
    endDate?: string
    branchId?: string
  }): AxiosPromise<Pagination.Pager<Invoices.InvoicePagination>> {
    return this.get('/fiscal-receipt-corrections', {
      params: {
        pageNumber,
        pageSize,
        search: search || null,
        clientId,
        paymentFilter: paymentFilter || 'ALL',
        sortingMode: sortingMode || 'NEWEST_TO_OLDEST',
        operatorName,
        startDate,
        endDate,
        branchId
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getSingleFiscalReceiptCorrection(
    fiscalReceiptNumber: string
  ): AxiosPromise<Invoices.Invoice> {
    return this.get(
      `/fiscal-receipt-corrections/single?number=${fiscalReceiptNumber}`,
      {
        authentication: Authentication.REQUIRED,
        aud: Audience.BACKOFFICE
      }
    )
  }

  createFiscalReceiptCorrection(payload: any) {
    return this.post('/fiscal-receipt-corrections', payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  createImportAccountingDocuments(
    documentType: string,
    documentNumber: string
  ): AxiosPromise<void> {
    return this.post(
      `/accounting-documents/import/file/${documentType}/${documentNumber}`,
      {},
      {
        authentication: Authentication.REQUIRED,
        aud: Audience.BACKOFFICE
      }
    )
  }

  getDocumentRequests(
    pageNumber: number,
    pageSize: number,
    byOperator: string | null,
    bySourceDocument: string[] | null,
    byStatus: string | null
  ): AxiosPromise<Documents.PagerDocumentRequestSummary> {
    return this.get('/document-requests', {
      params: {
        pageNumber,
        pageSize,
        byOperator,
        bySourceDocument,
        byStatus
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      paramsSerializer: function(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' })
      }
    })
  }

  requestInvoiceFromSalesOrder(
    number: string[]
  ): AxiosPromise<Documents.RequestsCreatedResponse> {
    return this.post(
      '/document-requests/request-invoice-from-sales-orders',
      {},
      {
        authentication: Authentication.REQUIRED,
        aud: Audience.BACKOFFICE,
        params: {
          number
        },
        paramsSerializer: function(params) {
          return qs.stringify(params, { arrayFormat: 'repeat' })
        }
      }
    )
  }

  requestInvoiceFromGin(
    number: string[],
    groupRequests?: boolean
  ): AxiosPromise<Documents.RequestsCreatedResponse> {
    return this.post(
      '/document-requests/request-invoice-from-goods-issued-notes',
      {},
      {
        authentication: Authentication.REQUIRED,
        aud: Audience.BACKOFFICE,
        params: {
          number,
          groupRequests
        },
        paramsSerializer: function(params) {
          return qs.stringify(params, { arrayFormat: 'repeat' })
        }
      }
    )
  }

  getClientsSettings(
    pageNumber: number,
    pageSize: number
  ): AxiosPromise<Documents.PagerClientDocumentRequestsView> {
    return this.get('/document-requests/client-settings', {
      params: {
        pageNumber,
        pageSize
      },
      aud: Audience.BACKOFFICE,
      authentication: Authentication.REQUIRED
    })
  }

  getClientSettings(
    clientId: string
  ): AxiosPromise<Documents.ClientDocumentRequestsSettings> {
    return this.get(`/document-requests/client-settings/${clientId}`, {
      aud: Audience.BACKOFFICE,
      authentication: Authentication.REQUIRED
    })
  }

  setClientType(
    clientId: string,
    payload: Documents.DocumentRequestClientSettingsUpdate
  ): AxiosPromise<void> {
    return this.post(
      `/document-requests/client-settings/${clientId}`,
      payload,
      {
        aud: Audience.BACKOFFICE,
        authentication: Authentication.REQUIRED
      }
    )
  }

  requestReceiptFromSalesOrder(
    number: string[]
  ): AxiosPromise<Documents.RequestsCreatedResponse> {
    return this.post(
      `/document-requests/request-receipt-from-sales-orders`,
      {},
      {
        aud: Audience.BACKOFFICE,
        authentication: Authentication.REQUIRED,
        params: {
          number
        },
        paramsSerializer: function(params) {
          return qs.stringify(params, { arrayFormat: 'repeat' })
        }
      }
    )
  }

  requestInvoiceCorrection(
    payload: Documents.InvoiceCorrectionRequestBody
  ): AxiosPromise<Documents.RequestsCreatedResponse> {
    return this.post('/document-requests/request-invoice-correction', payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getCorrectionsReason(): AxiosPromise<Documents.CorrectionsReasonDto[]> {
    return this.get('/document-requests/dictionaries/corrections-reason', {
      aud: Audience.BACKOFFICE,
      authentication: Authentication.REQUIRED
    })
  }

  handleCorrectionsReason(
    payload: Documents.CorrectionsReasonFormDto
  ): AxiosPromise<void> {
    return this.post(
      '/document-requests/dictionaries/corrections-reason',
      payload,
      {
        authentication: Authentication.REQUIRED,
        aud: Audience.BACKOFFICE
      }
    )
  }

  deleteCorrectionsReason(correctionsReasonId: string): AxiosPromise<void> {
    return this.delete(
      `/document-requests/dictionaries/corrections-reason/${correctionsReasonId}`,
      {
        authentication: Authentication.REQUIRED,
        aud: Audience.BACKOFFICE
      }
    )
  }

  getInvoicesWithCorrections({
    search,
    startDate,
    endDate,
    overdueDaysFrom,
    overdueDaysTo,
    correctionsFilter,
    onlyUnpaid,
    clientId,
    branchId,
    pageNumber,
    pageSize,
    sortingMode
  }: {
    search: string | null
    startDate?: string
    endDate?: string
    overdueDaysFrom?: number
    overdueDaysTo?: number
    correctionsFilter: Documents.CorrectionsFilterEnum
    onlyUnpaid?: boolean
    clientId?: string
    branchId?: string
    pageNumber: number
    pageSize: number
    sortingMode: Documents.SortingModeEnum
  }): AxiosPromise<Invoices.Backoffice.PagerInvoiceQueryInvoiceRestView> {
    return this.get('/invoice-query', {
      params: {
        search: search || null,
        startDate,
        endDate,
        overdueDaysFrom,
        overdueDaysTo,
        correctionsFilter: correctionsFilter || 'ALL',
        onlyUnpaid,
        clientId,
        branchId,
        pageNumber,
        pageSize,
        sortingMode: sortingMode || 'NEWEST_TO_OLDEST'
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getSingleInvoiceWithCorrections(
    invoiceNumber: string
  ): AxiosPromise<Invoices.Backoffice.InvoiceQueryInvoiceRestView> {
    return this.get(`/invoice-query/single?number=${invoiceNumber}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getInvoiceWithCorrectionsSummary({
    search,
    startDate,
    endDate,
    correctionsFilter,
    clientId,
    branchId,
    overdueDaysFrom,
    overdueDaysTo
  }: {
    search: string | null
    startDate?: string
    endDate?: string
    correctionsFilter: Documents.CorrectionsFilterEnum
    clientId?: string
    branchId?: string
    overdueDaysFrom?: number
    overdueDaysTo?: number
  }): AxiosPromise<Invoices.Backoffice.InvoiceQuerySummaryRestView> {
    return this.get('/invoice-query/summary', {
      params: {
        search,
        startDate,
        endDate,
        correctionsFilter: correctionsFilter || 'ALL',
        clientId,
        branchId,
        overdueDaysFrom,
        overdueDaysTo
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }
}
